
import { AccessRightModel } from "@/Domains/Auth";
import { ActivityModel } from "../Models";
import {
    DateFormatHelper,
    SoNumber,
    DetailItem,
    MediaItem,
    MediaType,
    DetailSection,
    AuditModel,
    ActivityEnums,
    dateCompare,
    AppEnumHelper,
    DOMAIN_TYPES,
    formatName
} from "@/Shared";
import { AuditViewModel } from "@/Shared/Logic/ViewModels/AuditViewModel";

const BACK_URL = process.env.REACT_APP_AMAT_API_URL;

export interface ActivityVMParams {
    activity: ActivityModel;
    viewEnums: ActivityEnums;
    accessRight: AccessRightModel;
    isDisplaying?: boolean;
}

/**
 * =====================================================
 * ActivityViewModel
 * =====================================================
 *
 * ViewModel representing an Activity entity for display
 * in detail views, cards, or lists.
 *
 * Responsibilities:
 * - Transform ActivityModel into display-friendly values
 * - Resolve enums for status, priority, and categories
 * - Format dates, numbers, and audit info
 * - Expose structured sections for detail pages
 * - Handle media attachments (images/videos)
 * - Compute display CSS classes and card titles
 *
 * Notes:
 * - Centralizes formatting and UI-facing transformations
 * - Decouples UI logic from raw ActivityModel data
 * - Works in conjunction with AccessRightModel for authorization checks
 *
 * Author: Samano CASTRE
 * Date: August 3, 2025
 */
export class ActivityViewModel {

    public id!: number;
    public model: ActivityModel;
    public taskId!: number;
    public taskCategory!: string;
    public taskLabel!: string;

    public status!: string;
    public statusClass!: string;

    public priority!: string;
    public priorityClass!: string;

    public completionRate!: number;
    public description!: string;
    public startDate!: string;
    public dueDate!: string;
    public expenseNote!: string;

    public initiatorId!: number;
    public initiatorUser!: string;

    public assignedAgentId!: number;
    public assignedAgent!: string;

    public cardHeaderTitle!: string;
    public cardClass!: string;


    public audit!: AuditViewModel;
    public isDeleted!: boolean;

    public constructor(protected params: ActivityVMParams) {
        const { activity } = params;
        this.audit = new AuditViewModel(activity as AuditModel);
        this.model = activity;
        this.initialize();
    }

    /**
     * Initializes all UI-facing properties
     * including labels, formatted dates, numbers, CSS classes, etc.
     */
    public initialize() {
        this.id = this.model?.id ?? 0;
        this.taskId = this.model?.taskId ?? 0;
        this.taskLabel = this.model?.task?.label ?? "";

        this.taskCategory = AppEnumHelper.getLabel(
            this.params.viewEnums.TaskCategoryEnum, 
            this.model.task?.category
        );

        this.setStatusCSSClass();
        this.setPriorityCSSClass();


        this.completionRate = this.model?.completionRate ?? 0;

        this.description = this.model?.description || "";

        this.startDate = DateFormatHelper.toLocaleDateString(this.model?.startDate) || "";
        this.dueDate = DateFormatHelper.toLocaleDateString(this.model?.dueDate) || "";

        this.expenseNote = SoNumber.toFR(this.model?.expenseNote ?? 0);

        this.assignedAgentId = this.model?.assignedTo ?? 0;
        this.assignedAgent = formatName(this.model.assignedAgent?.person);

        this.initiatorId = this.model?.initiatorId ?? 0;
        this.initiatorUser = formatName(this.model.initiatorUser?.person);

        this.priority = AppEnumHelper.getLabel(this.params.viewEnums.ActivityPriorityEnum, this.model.priority);
        this.status = AppEnumHelper.getLabel(this.params.viewEnums.ActivityStatusEnum, this.model.status);

        this.isDeleted = !!this.model.deletedAt;

        this.cardHeaderTitle = `${this.taskLabel} +  (ID : ${this.id})`;
        this.cardClass = this.params.isDisplaying ? 'active' : '';
    }


    private setPriorityCSSClass() {
        const priorityEnum = AppEnumHelper.getByValue(this.params.viewEnums.ActivityPriorityEnum, this.model.priority);
        this.priorityClass = "activity-priority--" + (priorityEnum?.key ?? "").toUpperCase();
    }

    private setStatusCSSClass() {
        const statusEnum = AppEnumHelper.getByValue(this.params.viewEnums.ActivityStatusEnum, this.model.status);
        this.statusClass = "activity-status--" + (statusEnum?.key ?? "").toUpperCase();
    }


    get title() {
        return "Activité N° " + this.id + " - " + this.taskLabel;
    }

    /**
     * Returns structured sections for detail view
     * including general info, dates, progress, and audit trail
     */
    getAllSections = (): DetailSection[] => {
        const sections: DetailSection[] = [
            {
                sectionFn: this.getGlobalSection,
                legend: 'Caractéristiques Générales',
                type: 'box'
            },
            {
                sectionFn: this.getDatesSection,
                legend: 'Dates & Participants',
                type: 'box'
            },
            {
                sectionFn: this.getProgressSection,
                legend: 'Progression & Statut',
                type: 'box'
            }
        ];
        if (this.params.accessRight.canAudit(DOMAIN_TYPES.Activity)) {
            sections.push(
                {
                    sectionFn: this.audit.getAuditTrail,
                    legend: 'Traçabilité',
                    type: 'box'
                }
            );
        }
        return sections;
    }

    getGlobalSection = () => {
        return [
            { label: "N° Activité", value: this.id },
            { label: "N° Tache", value: this.taskId },
            { label: "Catégorie", value: this.taskCategory },
            { label: "Type", value: this.taskLabel },
            { label: "Description", value: this.description }
        ] as DetailItem[];
    }

    getDatesSection = () => {
        return [
            { label: "Début", value: this.startDate },
            { label: "échéance", value: this.dueDate },
            { label: "Affectée à", value: this.assignedAgent },
            { label: "Donneur d'ordre", value: this.initiatorUser },

        ] as DetailItem[];

    }

    getProgressSection = () => {
        return [
            { label: "Priorité", value: this.priority },
            { label: "Statut", value: this.status },
            { label: "% Achevement", value: this.completionRate },
            { label: "Note de frais", value: this.expenseNote },

        ] as DetailItem[];
    }

    hasImage = () => (this.model?.activityMediaFiles ?? []).some(media => media.largeMedia?.fileType === "image");
    hasVideo = () => (this.model?.activityMediaFiles ?? []).some(media => media.largeMedia?.fileType === "video")

    getMediaItems = (): { images: MediaItem[], videos: MediaItem[] } => {

        const images = [] as MediaItem[];
        const videos = [] as MediaItem[];

        this.model?.activityMediaFiles.forEach(pm => {
            const isImage = pm.largeMedia?.fileType === 'image';

            const basePath = isImage ? BACK_URL + '/storage/' : '';
            const media = {
                type: pm.largeMedia?.fileType as MediaType,
                large: basePath + pm.largeMedia?.filePath,
                small: basePath + pm.largeMedia?.filePath,
                id: pm.id,
                caption: pm.caption
            } as MediaItem;

            isImage ? images.push(media) : videos.push(media);
        });

        return { images, videos };
    };

    /**
     * Comparator for sorting by creation date
     */
    compareTo(other: ActivityViewModel): number {
        return dateCompare(this.model.createdAt ?? "", other.model.createdAt ?? "");
    }
}